import React from 'react';
import { resolveWorkflowButtonColor } from 'src/utils';
import { resolveLifecycleColor } from 'src/app/workflows/workflowLifecycle';
import { IWorkflowLike } from 'src/app/workflows/useWorkflowStatusFilters';
import { IStatusSegment } from 'src/design/molecules/WorkflowStatusPanel';

/**
 * @registry Builds WorkflowStatusPanel segments from a list of workflow items, the ordered filter labels, the covered-status Set, and the job's current workflow (the latter two from useWorkflowStatusFilters): per-label counts plus an accent color (current-workflow step color first, else per-item-clone step color, else colorFallback entry, else lifecycle color, else gray). Pass colorFallback to color step-less statuses a module produces at stepIndex -1 (e.g. RFI 'More Information Needed'). 'All' counts every item and renders gray; the 'Other' pseudo-bucket counts items whose status is not in the covered Set and renders gray. Memoized on [items, filters, covered, currentWorkflow, colorFallback].
 */
export function useWorkflowStatusSegments<
  T extends {
    status?: string;
    workflow?: IWorkflowLike;
  },
>(
  items: T[],
  filters: string[],
  covered: ReadonlySet<string>,
  currentWorkflow?: IWorkflowLike,
  colorFallback?: Record<string, string>,
): IStatusSegment[] {
  return React.useMemo(() => {
    const countByLabel = new Map<string, number>();
    const colorByLabel = new Map<string, string>();

    for (const step of currentWorkflow?.steps ?? []) {
      if (step.label && !colorByLabel.has(step.label)) {
        const stepColor = resolveWorkflowButtonColor(step.buttonColor);
        if (stepColor) colorByLabel.set(step.label, stepColor);
      }
    }

    let otherCount = 0;
    for (const item of items) {
      if (item.status) {
        countByLabel.set(item.status, (countByLabel.get(item.status) ?? 0) + 1);
        if (!covered.has(item.status)) otherCount++;
      }
      for (const step of item.workflow?.steps ?? []) {
        if (step.label && !colorByLabel.has(step.label)) {
          const stepColor = resolveWorkflowButtonColor(step.buttonColor);
          if (stepColor) colorByLabel.set(step.label, stepColor);
        }
      }
    }

    return filters.map((label) => {
      if (label === 'All') {
        return {
          value: 'All',
          label: 'All',
          count: items.length,
          color: 'gray',
        };
      }
      // A template step literally named 'Other' is covered and falls through
      // to exact-match counting, mirroring useWorkflowStatusFilters.
      if (label === 'Other' && !covered.has('Other')) {
        return {
          value: 'Other',
          label: 'Other',
          count: otherCount,
          color: 'gray',
        };
      }
      return {
        value: label,
        label,
        count: countByLabel.get(label) ?? 0,
        color:
          colorByLabel.get(label) ??
          colorFallback?.[label] ??
          resolveLifecycleColor(label) ??
          'gray',
      };
    });
  }, [items, filters, covered, currentWorkflow, colorFallback]);
}
